import { Box } from '@mui/material';
import React from 'react';
import dayjs from 'dayjs';
import { useTelegram } from '../../../../hooks/useTelegram';
import { MEETING_ROOMS_TIMES } from '../../../../constants';
import { TableWrap, TimeLabel } from './styles';
import MeetingRoomCell from '../MeetingRoomCell/MeetingRoomCell';
import {
    calculateMaxRowSpan,
    endsInCell,
    getBookingRowSpan,
    overlapsCell,
    startsInCell,
} from './helpers';
import { CellBooking, MeetingRoomConfig } from '../../../../types/meetingRoomTable';
import { MeetingRoomBooking } from '../../../../types';

type MeetingRoomTableProps = {
    rooms: MeetingRoomConfig[];
    roomBookings: Record<string, CellBooking[]>;
    currentUserBookings: CellBooking[];
    formattedDate: string;
    onCellClick: (startTime: string, endTime: string, meetingRoomName: string) => void;
    editable?: boolean;
    onEditClick?: (
        booking: MeetingRoomBooking,
        meetingRoomName?: string,
        telegramUserId?: number,
    ) => void;
    onDeleteClick?: (bookingId: string) => void;
};

export const MeetingRoomTable: React.FC<MeetingRoomTableProps> = ({
    rooms,
    roomBookings,
    currentUserBookings,
    formattedDate,
    onCellClick,
    editable,
    onEditClick,
    onDeleteClick,
}) => {
    const { colorScheme } = useTelegram();

    return (
        <TableWrap roomCount={rooms.length}>
            {MEETING_ROOMS_TIMES.map((hour, hourIndex) => {
                const rowIndex = hourIndex + 1;
                const slotStartTime = `${hour.toString().padStart(2, '0')}:00:00`;
                const slotEndTime = `${hour + 1}:00:00`;

                return (
                    <React.Fragment key={hour}>
                        <Box gridColumn={1} gridRow={rowIndex}>
                            <TimeLabel
                                isFirst={hourIndex === 0}
                                isLast={hourIndex === MEETING_ROOMS_TIMES.length - 1}
                                colorScheme={colorScheme}
                            >
                                {dayjs().set('hours', hour).format('h a')}
                            </TimeLabel>
                        </Box>

                        {rooms.map((room, roomIndex) => {
                            const cellStart = dayjs(`${formattedDate} ${slotStartTime}`);
                            const cellEnd = cellStart.add(1, 'hour');

                            // All bookings that overlap this cell with continuation flag
                            const bookingsInCell: CellBooking[] = roomBookings[room.id]
                                .filter((b) => overlapsCell(b, cellStart, cellEnd))
                                .map((b) => ({
                                    ...b,
                                    isContinuing: !startsInCell(b, cellStart, cellEnd),
                                }));

                            // Current user's bookings in other rooms with continuation flag
                            const userOtherBookings: CellBooking[] = currentUserBookings
                                .filter((b) => b.meetingRoom?.id !== room.id)
                                .filter((b) => overlapsCell(b, cellStart, cellEnd))
                                .map((b) => ({
                                    ...b,
                                    isContinuing: !startsInCell(b, cellStart, cellEnd),
                                }));

                            const hasBookings = bookingsInCell.length > 0;
                            const hasUserOtherBooking = userOtherBookings.length > 0;

                            const isEmpty = !hasBookings && !hasUserOtherBooking;

                            // Check if cell has continuing bookings from above (hide top border)
                            const hideTopBorder = bookingsInCell.some((b) => b.isContinuing);
                            // Check if any booking continues beyond this cell (hide bottom border)
                            const hideBottomBorder = bookingsInCell.some(
                                (b) => !endsInCell(b, cellStart, cellEnd),
                            );

                            const bookingsRowSpan = hasBookings
                                ? Math.max(
                                      ...bookingsInCell.map((b) =>
                                          startsInCell(b, cellStart, cellEnd)
                                              ? calculateMaxRowSpan([b])
                                              : getBookingRowSpan(b, cellStart),
                                      ),
                                  )
                                : 1;
                            const userOtherBookingsRowSpan = hasUserOtherBooking
                                ? Math.max(
                                      ...userOtherBookings.map((b) =>
                                          startsInCell(b, cellStart, cellEnd)
                                              ? calculateMaxRowSpan([b])
                                              : getBookingRowSpan(b, cellStart),
                                      ),
                                  )
                                : 1;

                            const rowSpan = Math.max(bookingsRowSpan, userOtherBookingsRowSpan);

                            return (
                                <Box
                                    key={`${room.id}-${hour}`}
                                    gridColumn={roomIndex + 2}
                                    gridRow={isEmpty ? rowIndex : `${rowIndex} / span ${rowSpan}`}
                                    sx={{
                                        backgroundColor:
                                            colorScheme === 'dark' ? '#1F2023' : '#FFFFFF',
                                        minHeight: '69px',
                                    }}
                                >
                                    <MeetingRoomCell
                                        type={
                                            isEmpty ? 'empty' : hasBookings ? 'booked' : 'blocked'
                                        }
                                        cellStart={cellStart}
                                        bookings={bookingsInCell}
                                        userOtherBookings={userOtherBookings.map((b) => ({
                                            booking: b,
                                            user: b.userData,
                                        }))}
                                        onClick={(
                                            clickStartTime?: string,
                                            clickEndTime?: string,
                                        ) => {
                                            const finalStartTime = clickStartTime || slotStartTime;
                                            const finalEndTime = clickEndTime || slotEndTime;
                                            onCellClick(finalStartTime, finalEndTime, room.id);
                                        }}
                                        editable={editable}
                                        onEditClick={onEditClick}
                                        onDeleteClick={onDeleteClick}
                                        hideTopBorder={hideTopBorder}
                                        hideBottomBorder={hideBottomBorder}
                                    />
                                </Box>
                            );
                        })}
                    </React.Fragment>
                );
            })}
        </TableWrap>
    );
};

export default MeetingRoomTable;
